// typescript
import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { ClientFactory } from "@/main/common/ClientFactory";
import { onUserFollowAdded } from "@/main/common/EventRegistry";
import { Localizer } from "@/main/react/Types";
import { UserButtons } from "@/main/react/user/UserButtons";
import {
    NewsletterGetByEmailLine,
    pickMostSubscribedNewsletter,
    useMemberPublicNewsletters,
} from "@/main/react/newsletter/NewsletterGetByEmailLine";

/**
 * Asks the reader to follow this post's author, at the moment they have demonstrated affinity for
 * them. Design: designs/01-now/2026-09-06-follow-author-prompt-design.md
 *
 * <p>WHY THIS EXISTS. Members build the subscription object constantly and are never asked for it
 * where it would matter: 469 organic members added 2,347 follows in the 30 days to 2026-09-06, and
 * 1,314 of 1,703 repeat-reader pairs (77%) carry no follow edge. Until this component the ONLY place
 * to follow an author was their profile page or a participants card — never the post itself.
 *
 * <p>TWO TRIGGERS, and the primary one costs nothing to evaluate:
 * <ul>
 *   <li>T1, engagement: the reader just took a stance on this post or upvoted a thought by its
 *       author. The parent passes {@code engagementTriggered}. Measured at ~2,000 deduped
 *       (viewer, author) opportunities a week.</li>
 *   <li>T2, second read: the reader has already read another recent post by this author. Costs one
 *       bounded lazy request, and is only ~163 a week — a twelfth of T1's volume, which is why T1 is
 *       primary and why T2 can be deleted without the feature losing its point.</li>
 * </ul>
 *
 * <p>⚠️ THE AFFINITY REQUEST IS GATED ON EVERY CHEAP CONDITION FIRST, deliberately. It only fires for
 * a signed-in viewer who is not the author and does not already follow them, and never when T1 has
 * already opened the prompt. Moving that request earlier — onto the page-data payload or the SSR
 * path — would make every signed-in post view pay for an answer almost none of them use.
 *
 * <p>⚠️ RULE 23: this is NOT a control on the author byline. A metadata or attribution line earns a
 * link to the owning surface, never that object's action, which is the exact shape that shipped and
 * was reverted on 2026-08-30. This is a distinct, dismissible recommendation element with its own
 * copy — the same category as the inline recommendation cards that already carry a follow CTA — and
 * the author's profile remains the owning surface.
 *
 * <p>⚠️ RULE 32: additive only. It renders below server-rendered content and replaces nothing, so it
 * cannot leave the page with less than the HTML already carried.
 *
 * <p>MEASUREMENT: the prompt writes no relation row of its own. Every follow it produces is
 * identified solely by {@code surface="discussion_follow_prompt"} travelling through
 * {@code PUT /ajax/user/follow/{username}} into the FOLLOW_ADDED activity event. If that surface
 * string is renamed, the readout silently measures zero.
 */
export type FollowAuthorPromptProps = {
    /** The post's author. Blank disables the prompt entirely. */
    authorUsername: string;
    /** The post being read, for the affinity request. */
    discussionPublicId: string;
    /** The signed-in viewer, or null/blank when anonymous. */
    loggedInUsername: string | null | undefined;
    /** Server-resolved: does the viewer already follow the author? */
    initiallyFollowingAuthor: boolean;
    /**
     * Server-resolved moderation gate: may this author be recommended by name at all? False for a
     * DEMOTED or UNLISTED author and for a disabled account —
     * `RecommendedPublisherGateSql.existsPredicate`, projected onto the discussion page's own query.
     *
     * The caller passes it fail-CLOSED (absent reads as false), and the value must NEVER be defaulted
     * to true anywhere on the way here. Decision D3, 2026-09-08: the prompt is a recommendation, not
     * part of the listing. That a DEMOTED author's post page renders at all is the UNLISTED direct-link
     * exemption, which covers the PAGE and not an invitation rendered on it.
     */
    authorFollowableByPrompt: boolean;
    /** T1 — the viewer has just engaged with this author's content on this page. */
    engagementTriggered: boolean;
    localizer: Localizer;
};

export function FollowAuthorPrompt(props: FollowAuthorPromptProps) {
    const {
        authorUsername,
        discussionPublicId,
        loggedInUsername,
        initiallyFollowingAuthor,
        authorFollowableByPrompt,
        engagementTriggered,
        localizer,
    } = props;

    const [dismissed, setDismissed] = useState(false);
    const [followed, setFollowed] = useState(initiallyFollowingAuthor);
    const [affinityTriggered, setAffinityTriggered] = useState(false);

    // Every cheap condition, evaluated before anything asks the server. `followed` starts from the
    // server flag and flips as soon as the viewer follows from anywhere on the page, so the prompt
    // disappears the moment it has done its job.
    const viewerIsSignedIn = Boolean(loggedInUsername && loggedInUsername.trim());
    const viewerIsAuthor = Boolean(
        authorUsername && loggedInUsername && authorUsername === loggedInUsername,
    );
    // The moderation gate joins the cheap conditions, ahead of everything that costs a request: a
    // moderated author must not even produce the T2 affinity call, let alone the prompt.
    const eligible =
        Boolean(authorUsername) && authorFollowableByPrompt && viewerIsSignedIn && !viewerIsAuthor
        && !followed && !dismissed;

    // D12 (newsletter growth work order, slice 2c): one "Get {name} by email" line INSIDE this prompt, for
    // the author's most-subscribed public newsletter. Asked only once the prompt is actually showing, so a
    // post view that never opens the prompt pays for no newsletter read either (the same economy as the
    // affinity request below). `eligible` already excludes the author and anonymous viewers.
    const promptShowing = eligible && (engagementTriggered || affinityTriggered);
    const authorNewsletters = useMemberPublicNewsletters(authorUsername, promptShowing);
    const authorNewsletter = pickMostSubscribedNewsletter(authorNewsletters);

    // The prompt closes itself when the follow lands, including a follow made from the participants
    // card or anywhere else on the page — the same cross-island event UserButtons already emits.
    useEffect(() => {
        return onUserFollowAdded((payload) => {
            if (payload?.targetUsername === authorUsername) {
                setFollowed(true);
            }
        });
    }, [authorUsername]);

    useEffect(() => {
        setFollowed(initiallyFollowingAuthor);
    }, [initiallyFollowingAuthor]);

    // T2. Gated on `eligible` so an anonymous viewer, the author, or somebody who already follows
    // never issues it; gated on `!engagementTriggered` so T1 opening the prompt does not also spend a
    // request answering a question whose answer no longer changes anything.
    useEffect(() => {
        if (!eligible || engagementTriggered || affinityTriggered || !discussionPublicId) {
            return;
        }
        let cancelled = false;
        // ⚠️ try/catch AROUND the call, not only `.catch()` on the promise. A rejected request is
        // handled below; a SYNCHRONOUS throw here is a different failure and a worse one — it escapes
        // the effect, unmounts the DiscussionPage island, and leaves the reader with less than the
        // server-rendered HTML already contained (rule 32). This whole trigger is an offer, not
        // content: every way it can fail must end with the prompt simply not appearing.
        try {
            ClientFactory.getAuthorAffinity(discussionPublicId)
                .then((response) => {
                    if (!cancelled && response?.data?.hasReadAnotherPostByAuthor) {
                        setAffinityTriggered(true);
                    }
                })
                .catch(() => {
                    // Best effort by design: the prompt is an offer, not content. If this fails the
                    // reader simply does not see it, and nothing they were shown is taken away.
                });
        } catch {
            // As above.
        }
        return () => {
            cancelled = true;
        };
    }, [eligible, engagementTriggered, affinityTriggered, discussionPublicId]);

    if (!promptShowing) {
        return null;
    }

    return (
        <section
            className="mt-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-4"
            data-playwright-id="follow-author-prompt"
            data-testid="follow-author-prompt"
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-foreground">
                        {localizer("discussion.follow-prompt.title")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {localizer("discussion.follow-prompt.body")}
                    </p>
                </div>
                <div className="flex shrink-0 flex-row items-center gap-2">
                    {/*
                     * The shared primitive, not a hand-rolled <button>: until 2026-09-07 this was a raw
                     * button on none of the four sanctioned tap paths, so it had no focus ring at all and
                     * no coarse-pointer floor — on the busiest page in the product, beside a CTA that has
                     * both. `ghost` supplies the one hover wash and `buttonVariants` the shared
                     * focus-visible ring; the extra class adds the 44px touch floor that the text sizes
                     * (h-8) do not reach on their own.
                     */}
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="touch-manipulation [@media(pointer:coarse)]:min-h-11 text-muted-foreground"
                        data-playwright-id="follow-author-prompt-dismiss"
                        onClick={() => setDismissed(true)}
                    >
                        {localizer("discussion.follow-prompt.dismiss")}
                    </Button>
                    <UserButtons
                        username={authorUsername}
                        initiallyFollowing={false}
                        localizer={localizer}
                        displayMode="primary-only"
                        allowWrap={false}
                        surface="discussion_follow_prompt"
                    />
                </div>
            </div>
            {/* D12: the newsletter line, one row below the follow offer and inside the same card (never on
                the byline, rule 23 bullet 6). Absent until the read answers with a public newsletter. */}
            {authorNewsletter ? (
                <NewsletterGetByEmailLine
                    newsletter={authorNewsletter}
                    localizer={localizer}
                    loggedIn={viewerIsSignedIn}
                    surface="post_page"
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3"
                />
            ) : null}
        </section>
    );
}
